/**
 * Vehicle-map helpers: pick-up/trunk vs delivery role, and hub handoff pins.
 */

import { standardiseVehicleId } from './vehicleIdStandardise';

export type OrderVehicleMapRole = 'pickup' | 'delivery' | 'unknown';

export type OrgDepotForMap = {
  name: string;
  latitude: number;
  longitude: number;
  depotNumber?: string | null;
  postcode?: string;
  owned?: boolean;
  id?: string;
};

export type OrderHandoffMapPoint = {
  orderId: string;
  lat: number;
  lon: number;
  /** e.g. "Depot 102" */
  label: string;
  postcode?: string;
  /** Optional richer site name from org config */
  depotName?: string;
};

export function formatDepotHandoffLabel(depot: Pick<OrgDepotForMap, 'name' | 'depotNumber'>): string {
  const num = String(depot.depotNumber ?? '').trim();
  if (num) return `Depot ${num}`;
  const name = String(depot.name ?? '').trim();
  return name || 'Hub';
}

/**
 * Role of this vehicle on the order for map pins.
 * Prefer vehicle-day pickup/delivery ID lists; fall back to plate fields.
 * When an order appears in both lists, prefer delivery if this vehicle is the
 * delivery plate, else pickup if pick-up plate, else unknown.
 */
export function orderRoleOnVehicleMap(
  order: {
    orderId?: string;
    vehicle_pickup?: string | null;
    vehicle_observed?: string | null;
    vehicleId?: string | null;
  },
  modalVehicleId: string,
  pickupOrderIds: ReadonlySet<string>,
  deliveryOrderIds: ReadonlySet<string>,
): OrderVehicleMapRole {
  const oid = String(order.orderId ?? '').trim();
  const inPickup = oid !== '' && pickupOrderIds.has(oid);
  const inDelivery = oid !== '' && deliveryOrderIds.has(oid);

  const vid = standardiseVehicleId(modalVehicleId) || modalVehicleId.trim().toUpperCase();
  const pu = standardiseVehicleId(order.vehicle_pickup ?? '') || '';
  const del =
    standardiseVehicleId(order.vehicle_observed ?? order.vehicleId ?? '') || '';

  if (inPickup && inDelivery) {
    if (del && del === vid) return 'delivery';
    if (pu && pu === vid) return 'pickup';
    return 'unknown';
  }
  if (inPickup && !inDelivery) return 'pickup';
  if (inDelivery && !inPickup) return 'delivery';

  if (pu && pu === vid && del && del !== vid) return 'pickup';
  if (del && del === vid) return 'delivery';
  if (pu && pu === vid && (!del || del === vid)) return 'delivery'; // sole carrier
  return 'unknown';
}

function validCoord(lat: number | null | undefined, lon: number | null | undefined): boolean {
  return (
    lat != null
    && lon != null
    && Number.isFinite(lat)
    && Number.isFinite(lon)
    && lat !== 0
    && lon !== 0
  );
}

function findOrgDepotByNumber(
  depots: OrgDepotForMap[],
  depotNumber: string,
): OrgDepotForMap | null {
  const wanted = depotNumber.trim();
  if (!wanted) return null;

  // Prefer explicit depotNumber from org config.
  for (const depot of depots) {
    if (String(depot.depotNumber ?? '').trim() !== wanted) continue;
    if (!validCoord(depot.latitude, depot.longitude)) continue;
    return depot;
  }

  // Fallback: name/id already encodes the network number (e.g. "Depot 102")
  // when Control Centre has coords but depotNumber was left blank.
  const nameRe = new RegExp(`(?:^|\\b)(?:depot\\s*)?${wanted.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
  for (const depot of depots) {
    if (!validCoord(depot.latitude, depot.longitude)) continue;
    const name = String(depot.name ?? '');
    const id = String((depot as { id?: string }).id ?? '');
    if (nameRe.test(name) || nameRe.test(id)) return depot;
  }
  return null;
}

/**
 * Resolve hub handoff for a pick-up/trunk vehicle-day.
 *
 * Placement is driven only by truth-layer getnotes depot number
 * (`handoff_depot` / edge `properties.depot`), matched to org depot
 * coordinates by depot number — never by proximity to telematics End.
 */
export function resolveOrderHandoffMapPoint(
  orderId: string,
  depots: OrgDepotForMap[],
  truthDepotNumber?: string | null,
): OrderHandoffMapPoint | null {
  const depotNum = String(truthDepotNumber ?? '').trim();
  if (!depotNum) return null;

  const match = findOrgDepotByNumber(depots, depotNum);
  if (!match) return null;

  return {
    orderId,
    lat: match.latitude,
    lon: match.longitude,
    label: formatDepotHandoffLabel({
      name: match.name,
      // Prefer org depotNumber; else the truth number we matched on.
      depotNumber: match.depotNumber || depotNum,
    }),
    postcode: match.postcode,
    depotName: match.name,
  };
}

/**
 * Map fleet-intelligence /depots JSON into map-ready org depot pins.
 * Drops rows without finite non-zero coordinates.
 */
export function mapOrgDepotsFromApiPayload(json: unknown): OrgDepotForMap[] {
  const raw =
    json != null && typeof json === 'object' && Array.isArray((json as { depots?: unknown }).depots)
      ? (json as { depots: unknown[] }).depots
      : [];
  return raw
    .filter((d): d is Record<string, unknown> => d != null && typeof d === 'object')
    .map((d) => ({
      id: d.id != null ? String(d.id).trim() : undefined,
      name: String(d.name ?? '').trim(),
      latitude: Number(d.latitude),
      longitude: Number(d.longitude),
      depotNumber: d.depotNumber != null ? String(d.depotNumber).trim() : null,
      postcode: d.postcode != null ? String(d.postcode).trim() : undefined,
      owned: d.owned === true || d.owned === 1 || d.owned === '1' || d.owned === 'true',
    }))
    .filter(
      (d) =>
        Number.isFinite(d.latitude)
        && Number.isFinite(d.longitude)
        && d.latitude !== 0
        && d.longitude !== 0,
    );
}
